import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PencilIcon} from '@primer/octicons-react'
import {IconButton, PageHeader} from '@primer/react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {PullRequestBanners} from './PullRequestBanners'
import {FilesChangedFeedback} from './FilesChangedFeedback'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import {StackState} from './StackState'
import {StackNavigationCommands} from './StackNavigationCommands'
import type {AliveChannels, HeaderPageData} from '../page-data/payloads/header'
import {FeatureState} from '../page-data/payloads/header'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat, DiffStatLoadingSkeleton} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {isPullRequestOpenState} from '../utils/pull-request-state'
import {StickyPullRequestHeader, type StickyPullRequestHeaderProps} from './StickyPullRequestHeader'
import {setTitle} from '@github-ui/document-metadata'
import {PullRequestsTopBanners} from './banners/PullRequestsTopBanners'
import {
  DockedPanel,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
  useDockedPanelActions,
} from '../contexts/DockedPanelContext'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {useHydrateMarkerCounts} from '../hooks/use-hydrate-marker-counts'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import {getRouteConfig} from '../routes/route-manager'
import {OverviewFeatureToggle, useOverviewToggleRenderState} from './overview/feature-preview/OverviewFeatureToggle'
import {useFeatureState} from '../hooks/use-feature-state'
import {ReviewMenuButton} from './toolbar/ReviewMenuButton'
import {OpenCopilotChatPanelButton} from './toolbar/OpenCopilotChatPanelButton'
import {OpenDockedPanelButton} from './toolbar/OpenDockedPanelButton'
import {OpenCommentsPanelButton} from './toolbar/OpenCommentsPanelButton'
import {OpenAlertsPanelButton} from './toolbar/OpenAlertsPanelButton'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {useUnifiedBatchCount} from '../stores/layout-selectors'
import {ManageBatchHeaderButton} from './toolbar/ManageBatchHeaderButton'

/**  HelpUrl is omitted to move away from prop drilling route, page, and query data
See: https://github.com/github/github-ui/blob/main/.github/instructions/pull-requests-overview.instructions.md */
export type PullRequestHeaderProps = Omit<HeaderPageData, 'helpUrl'> & {
  activeRoute: PullRequestActiveRoute
  stickyHeaderVariant?: StickyPullRequestHeaderProps['pageVariant']
  aliveChannels: AliveChannels
}

export function PullRequestHeader({
  aliveChannels,
  bannersData: initialBannersData,
  activeRoute,
  featureStates,
  stickyHeaderVariant,
  pullRequest: initialPullRequest,
  pageTitle: initialPageTitle,
  repository: initialRepository,
  urls: initialUrls,
  user: initialUser,
  mergeStatusButtonData: initialMergeStatusButtonData,
  stack: initialStack,
  viewSettings,
}: PullRequestHeaderProps) {
  const {helpUrl} = useLayoutRouteData()

  const {data: headerData} = useHeaderPageData({
    aliveChannels,
    bannersData: initialBannersData,
    featureStates,
    helpUrl,
    pageTitle: initialPageTitle,
    pullRequest: initialPullRequest,
    repository: initialRepository,
    stack: initialStack,
    urls: initialUrls,
    user: initialUser,
    mergeStatusButtonData: initialMergeStatusButtonData,
    viewSettings,
  })
  const {bannersData, pullRequest, repository, pageTitle, urls, user, mergeStatusButtonData, stack} = headerData
  const {dockedPanelSupported} = getRouteConfig(activeRoute)
  const [isEditing, setIsEditing] = useState(false)
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const showMiniHeader = isFeatureEnabled('prs_overview_mini_header')
  const unifiedBatchPrCommentsEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const navigationShortcutsEnabled = isFeatureEnabled('pull_request_stacks_navigation_shortcuts')

  const shouldShowLoggedInOptions = isLoggedIn()
  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const isPROpen = isPullRequestOpenState(pullRequest.state)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const hasValidatedMergeData = validatedMergeData !== null
  const batchCount = useUnifiedBatchCount()

  const sidePanelOpen = useIsDockedPanelOpen()
  const isPanelCompact = useIsDockedPanelCompact()
  const panelOpenOnDesktop = sidePanelOpen && !isPanelCompact

  // Docked merge status panel — only active on files/changes routes
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  useHydrateMarkerCounts()

  // Auto-close the docked merge status panel when the PR is merged or closed.
  // After merge, validatedMergeData becomes null which unmounts MergeStatusButton
  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  useEffect(() => {
    if (pageTitle) {
      setTitle(pageTitle)
    }
  }, [pageTitle])

  // Primer's narrow viewport range
  const feedbackRenderLocation: 'mobile' | 'desktop' = useMediaQuery('(max-width: calc(48rem - 0.02px))')
    ? 'mobile'
    : 'desktop'

  const isFilesChangedRoute = activeRoute === 'files_changed'
  const renderOverviewFeatureToggle = useOverviewToggleRenderState() !== 'none'
  const renderOldHeaderLoggedInOptions = shouldShowLoggedInOptions && !showUpdatedHeader
  const renderUpdatedHeaderLoggedInOptions = shouldShowLoggedInOptions && showUpdatedHeader
  const showManageBatchHeaderButton = dockedPanelSupported && unifiedBatchPrCommentsEnabled && batchCount > 0
  const hasActionsContent =
    isFilesChangedRoute ||
    renderOldHeaderLoggedInOptions ||
    renderUpdatedHeaderLoggedInOptions ||
    renderOverviewFeatureToggle

  return (
    <>
      {isPullRequestStacksEnabled && navigationShortcutsEnabled && stack && (
        <StackNavigationCommands pullRequestNumber={pullRequest.number} stack={stack} />
      )}
      {/** Used by suggested-changes.ts to disable the "suggested changes" UI when the PR is closed */}
      <div className="js-pull-header-details" data-pull-is-open={isPROpen ? 'true' : 'false'} hidden />
      <PullRequestsTopBanners
        bannersData={bannersData}
        urls={urls}
        activeRoute={activeRoute}
        pullRequestId={pullRequest.id}
        pullRequestNumber={pullRequest.number}
        baseBranch={pullRequest.baseBranch}
      />
      {isEditing && (
        <div
          className={clsx(
            showUpdatedHeader && styles.editTitleFormUpdatedHeader,
            showUpdatedHeader && sidePanelOpen && styles.editTitleFormSidePanelOpen,
            showUpdatedHeader && styles.constrainedHeader,
          )}
        >
          <PullRequestEditTitleForm
            initialTitle={pullRequest.title}
            pullRequestNumber={pullRequest.number}
            onCloseForm={() => setIsEditing(false)}
          />
        </div>
      )}
      <div className={clsx(showUpdatedHeader && styles.headerBorderBottom)} data-side-panel-open={panelOpenOnDesktop}>
        <PageHeader
          className={clsx(
            'flex-items-center',
            showUpdatedHeader && styles.constrainedHeader,
            showUpdatedHeader && styles.headerContainer,
            showUpdatedHeader && !isEditing && 'tmp-mt-3',
            sidePanelOpen && styles.sidePanelOpen,
            sidePanelOpen && showUpdatedHeader && styles.sidePanelOpenUpdatedHeader,
            styles.actionsAboveTitleOnNarrow,
            styles.actionsAboveTitleOnNarrowContainer,
          )}
        >
          {isEditing && (
            // this is done so that we still have an h1 element present when editing the title
            <h1 className="sr-only">{`${pullRequest.title} - #${pullRequest.number}`}</h1>
          )}
          {!isEditing && (
            <>
              <PageHeader.TitleArea>
                <div className={styles.titleWithAction}>
                  <PageHeader.Title
                    as="h1"
                    className={clsx('lh-condensed', styles.inlineTitle, showUpdatedHeader && styles.title)}
                  >
                    <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                    <span className="sr-only"> - #{pullRequest.number}</span>
                  </PageHeader.Title>
                  <span className={styles.titleSuffix}>
                    <span aria-hidden="true" className="pl-2 fgColor-muted f1-light lh-condensed">
                      #{pullRequest.number}
                    </span>
                    {shouldShowLoggedInOptions && user.canEditTitle && (
                      <IconButton
                        icon={PencilIcon}
                        onClick={() => setIsEditing(true)}
                        size="medium"
                        aria-label="Edit title"
                        variant="invisible"
                        className="ml-1"
                      />
                    )}
                  </span>
                </div>
              </PageHeader.TitleArea>
              <PageHeader.Actions
                className={clsx(
                  'flex-items-center gap-2 position-relative',
                  showUpdatedHeader && 'flex-justify-end',
                  !hasActionsContent && 'd-none',
                )}
              >
                {isFilesChangedRoute && (
                  <FilesChangedFeedback repository={repository} urls={urls} renderLocation={feedbackRenderLocation} />
                )}
                {renderOverviewFeatureToggle && <OverviewFeatureToggle />}
                {renderUpdatedHeaderLoggedInOptions && (
                  <div className="d-flex gap-2">
                    <PullRequestCodeButton
                      codespacesEnabled={repository.codespacesEnabled}
                      headBranch={pullRequest.headBranch}
                      isEnterprise={repository.isEnterprise}
                      pullRequestId={pullRequest.id}
                      pullRequestNumber={pullRequest.number}
                      repository={repository}
                      size={'medium'}
                      trailingVisual
                      iconOnly
                    />
                    {!showMiniHeader &&
                      currentUserLogin &&
                      (showManageBatchHeaderButton ? (
                        <ManageBatchHeaderButton
                          count={batchCount}
                          entrySurface="header_manage_batch_button"
                          size="medium"
                        />
                      ) : (
                        <ReviewMenuButton
                          currentUserLogin={currentUserLogin}
                          pullRequest={pullRequest}
                          headRefOid={pullRequest.headSha}
                          repository={repository}
                          overlayPortal="external"
                          redirectOnMutation={!(activeRoute === 'conversation' || activeRoute === 'overview')}
                          stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
                          size="medium"
                        />
                      ))}
                    {!showMiniHeader && <OpenDockedPanelButton size="medium" />}
                  </div>
                )}
                {renderOldHeaderLoggedInOptions && (
                  <div className="d-flex gap-2">
                    {validatedMergeData && (
                      <MergeStatusButtonWrapper
                        channels={validatedMergeData.channels}
                        headSha={validatedMergeData.headSha}
                        helpUrl={helpUrl}
                        viewerLogin={validatedMergeData.viewerLogin}
                        isDockedMode={isDockedMode}
                        onToggleDockedPanel={handleToggleDockedMergePanel}
                        mergeStatusButtonRef={mergeStatusButtonRef}
                      />
                    )}
                    <PullRequestCodeButton
                      codespacesEnabled={repository.codespacesEnabled}
                      headBranch={pullRequest.headBranch}
                      isEnterprise={repository.isEnterprise}
                      pullRequestId={pullRequest.id}
                      pullRequestNumber={pullRequest.number}
                      repository={repository}
                      size={'medium'}
                    />
                  </div>
                )}
              </PageHeader.Actions>
            </>
          )}
          <PageHeader.Description className={'d-flex flex-column flex-items-start'}>
            <div
              className={clsx(
                'd-flex width-full',
                showUpdatedHeader
                  ? 'flex-items-center gap-2 flex-wrap min-width-0'
                  : 'flex-items-start gap-2 flex-column flex-sm-row flex-justify-between',
              )}
            >
              <div className="d-flex flex-items-center gap-2 flex-shrink-0">
                <PullRequestStateLabel state={pullRequest.state} />
                <StackState pullRequestNumber={pullRequest.number} stack={stack} viewerCanUnstack={user.canEditTitle} />
              </div>
              <div className={showUpdatedHeader ? undefined : 'flex-1 overflow-hidden'}>
                <PullRequestHeaderSummary
                  author={pullRequest.author}
                  attributedUser={pullRequest.attributedUser}
                  mergedTime={pullRequest.mergedTime}
                  baseBranch={pullRequest.baseBranch}
                  baseRepositoryDefaultBranch={repository.defaultBranch}
                  baseRepositoryName={repository.name}
                  baseRepositoryOwnerLogin={repository.ownerLogin}
                  canChangeBase={user.canChangeBase}
                  commitsCount={pullRequest.commitsCount}
                  headBranch={pullRequest.headBranch}
                  headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                  headRepositoryName={pullRequest.headRepositoryName}
                  isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  isEditing={isEditing}
                  mergedBy={pullRequest.mergedBy}
                  mergedByAvatarUrl={pullRequest.mergedByAvatarUrl}
                  mergedByName={pullRequest.mergedByName}
                  repositoryId={repository.id}
                  pullRequestId={pullRequest.id}
                  pullRequestNumber={pullRequest.number}
                  setIsEditing={setIsEditing}
                  state={pullRequest.state}
                  stack={stack}
                />
              </div>
            </div>
            <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
          </PageHeader.Description>
          <PageHeader.Navigation className="tmp-pt-3">
            <div
              className={clsx(
                styles.rightContentWrapper,
                showUpdatedHeader ? styles.hideWhenTabsOverflow : styles.hideWhenVeryNarrowContainer,
                showUpdatedHeader && styles.reducedTopPadding,
              )}
            >
              {showUpdatedHeader ? (
                <>
                  {!showMiniHeader && shouldShowLoggedInOptions && validatedMergeData && (
                    <MergeStatusButtonWrapper
                      channels={validatedMergeData.channels}
                      headSha={validatedMergeData.headSha}
                      helpUrl={helpUrl}
                      viewerLogin={validatedMergeData.viewerLogin}
                      isDockedMode={isDockedMode}
                      onToggleDockedPanel={handleToggleDockedMergePanel}
                      mergeStatusButtonRef={mergeStatusButtonRef}
                      variant="invisible"
                      size="medium"
                      showTrailingArrow
                    />
                  )}
                  {!showMiniHeader && (
                    <OpenCommentsPanelButton
                      buttonClasses={styles.squareButton}
                      size="medium"
                      showMarkerCounts={false}
                    />
                  )}
                  <OpenAlertsPanelButton
                    buttonClasses={styles.squareButton}
                    size="medium"
                    showMarkerCounts={false}
                    hideIfNoAlerts
                  />
                  {!showMiniHeader && <OpenCopilotChatPanelButton size="medium" variant="invisible" />}
                </>
              ) : (
                <ErrorBoundary boundaryName="PullRequestHeaderDiffStat" fallback={null}>
                  <Suspense fallback={<DiffStatLoadingSkeleton />}>
                    <DiffStat />
                  </Suspense>
                </ErrorBoundary>
              )}
            </div>
            <div className="flex-auto">
              <PullRequestHeaderNavigation
                activeRoute={activeRoute}
                pullRequest={pullRequest}
                repository={repository}
                urls={urls}
              />
            </div>
          </PageHeader.Navigation>
        </PageHeader>
      </div>
      {!showUpdatedHeader && !isFilesChangedRoute && (
        <StickyPullRequestHeader
          viewerCanUnstack={user.canEditTitle}
          repository={repository}
          pullRequest={pullRequest}
          pageVariant={stickyHeaderVariant ?? 'condensed'}
          stack={stack}
        />
      )}
    </>
  )
}
